/** Typing a cave's name and hint (docs/spec/03 §1: names are A-Z 0-9 space . ! ' -). Pure. */

export type TextField = 'name' | 'hint';

export const TEXT_MAX: Readonly<Record<TextField, number>> = { name: 16, hint: 60 };

const NAME_CHAR = /^[A-Z0-9 .!'-]$/;
/** Everything the 8 x 8 font draws (lower case shows as upper case). */
const HINT_CHAR = /^[A-Za-z0-9 .,:;!?'"+\-/()%#*=<>_&@[\]]$/;

/** `text` with the typed `key` appended, or null when the key is not allowed (or the text is full). */
export function typeChar(field: TextField, text: string, key: string): string | null {
  if (key.length !== 1 || text.length >= TEXT_MAX[field]) return null;
  const ch = field === 'name' ? key.toUpperCase() : key;
  if (!(field === 'name' ? NAME_CHAR : HINT_CHAR).test(ch)) return null;
  // No leading space, and no double spaces in a name: they only make it look broken.
  if (ch === ' ' && (text.length === 0 || (field === 'name' && text.endsWith(' ')))) return null;
  return text + ch;
}

/** A name that passes validation, from anything: upper case, allowed characters, trimmed, never empty. */
export function tidyName(name: string): string {
  const clean = [...name.toUpperCase()].filter((c) => NAME_CHAR.test(c)).join('').replace(/ +/g, ' ').trim();
  return clean.slice(0, TEXT_MAX.name).trim() || 'CAVE';
}

/** A name for a copy: `CAVE` -> `CAVE 2`, `CAVE 2` -> `CAVE 3`, within 16 characters. */
export function copyName(name: string): string {
  const m = /^(.*?) (\d+)$/.exec(name);
  const stem = (m ? m[1] : name) ?? name;
  const n = m ? Number(m[2]) + 1 : 2;
  const suffix = ` ${n}`;
  return tidyName(stem.slice(0, TEXT_MAX.name - suffix.length).trimEnd() + suffix);
}
